import {FC, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useField} from 'formik';
import {FormikSwitcher} from '@tickchak/react-share/app';
import Input from '../../../../../component/input/Input';
import NumberInput from '../../../../../component/input/NumberInput';
import InputAmount from '../../../../../component/input/inputAmount/InputAmount';
import TextEditorFormik from '../../../../../component/textEditor/TextEditorFormik';
import {TShow} from '../../../../../hooks/useShow';
import Pencil from '../../../../../assets/icons/Pencil';
import SectionBox from '../SectionBox/SectionBox';
import LecturersSection from '../fields/LecturersSection';
import HallField from '../fields/HallField';
import TimeSlotField from '../fields/TimeSlotField';
import DayDateField from '../fields/DayDateField';
import {getLecturers, getHalls, getSlots} from '../../../../../redux/timetable/timetableActions';
import {RootState} from '../../../../../redux/store.type';
import * as css from './DetailsTab.css';

interface DetailsTabProps {
    lecturersShow: TShow;
    hallShow: TShow;
    slotShow: TShow;
    dayShow: TShow;
}

const CasualPriceField: FC = () => {
    const {t} = useTranslation('timetable');
    const [field] = useField<boolean>('hasCasualPrice');
    const enabled = !!field.value;

    return (
        <css.casualPriceWrap>
            <css.casualPriceToggle>
                <FormikSwitcher
                    name="hasCasualPrice"
                    label={{on: t('toggleActive'), off: t('toggleInactive'), show: true}}
                />
            </css.casualPriceToggle>
            <InputAmount
                name="priceCasual"
                text={t('casualCustomerPrice')}
                icon="walletOutline"
                disabled={!enabled}
                className=""
                forwardRef={null}
            />
        </css.casualPriceWrap>
    );
};

const DetailsTab: FC<DetailsTabProps> = ({lecturersShow, hallShow, slotShow, dayShow}) => {
    const {t} = useTranslation('timetable');
    const lecturers = useSelector((state: RootState) => state.timetable.lecturers);
    const halls = useSelector((state: RootState) => state.timetable.halls);
    const slots = useSelector((state: RootState) => state.timetable.slots);

    useEffect(() => {
        if(!lecturers.length) getLecturers();
        if(!halls.length) getHalls();
        if(!slots.length) getSlots();
    }, []);

    return (
        <>
            <SectionBox title={t('sectionLectureDetails')}>
                <Input
                    name="title"
                    text={t('lectureName')}
                    icon="personOutline"
                    events={{}}
                    className=""
                    forwardRef={null}
                />
                <css.fieldsThreeCols>
                    <HallField show={hallShow} />
                    <DayDateField show={dayShow} />
                    <TimeSlotField show={slotShow} />
                </css.fieldsThreeCols>
            </SectionBox>

            <SectionBox title={t('lecturersTeam')}>
                <LecturersSection show={lecturersShow} />
            </SectionBox>

            <SectionBox title={t('sectionPricing')}>
                <css.fieldsTwoCols>
                    <InputAmount
                        name="price"
                        text={t('additionalPayment')}
                        icon="walletOutline"
                        amountText="₪"
                        helper={t('additionalPaymentHelper')}
                        className=""
                        forwardRef={null}
                    />
                    <NumberInput
                        name="amount"
                        text={t('participantsCount')}
                        icon="calendarOutline"
                        className=""
                        forwardRef={null}
                        events={{autoComplete: 'off'}}
                    />
                </css.fieldsTwoCols>
                <CasualPriceField />
            </SectionBox>

            <SectionBox title={t('sectionMoreInfo')} subtitle={t('sectionMoreInfoSubtitle')}>
                <css.summaryField>
                    <css.summaryHeader>
                        <css.summaryIcon>
                            <Pencil />
                        </css.summaryIcon>
                        <css.summaryLabel>{t('summaryPlaceholder')}</css.summaryLabel>
                    </css.summaryHeader>
                    <css.editorWrap>
                        <TextEditorFormik
                            name="description"
                            showToolbar
                            limit={{maxChar: 1000, showCharCounter: true}}
                            minHeight="180px"
                            placeholder={t('summaryPlaceholder')}
                        />
                    </css.editorWrap>
                </css.summaryField>
            </SectionBox>
        </>
    );
};

export default DetailsTab;
